import { useEffect, useRef, useState } from 'react';

import Icon from '../../../components/icon/Icon';
import { KEY } from '../../../utilities/events';

// How long the "just changed" tick stays beside a committed value before fading.
const CHECK_DURATION_MS = 2500;

type EditableCellProps = {
  value: number;
  display: string;
  onCommit: (value: number) => void;
};

// Label that swaps to an input on click; commits on blur/Enter so the parent's
// dirty state reflects committed edits, not keystrokes.
const EditableCell = ({ value, display, onCommit }: EditableCellProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [justChanged, setJustChanged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  useEffect(() => {
    if (!justChanged) return;
    const timer = setTimeout(() => setJustChanged(false), CHECK_DURATION_MS);
    return () => clearTimeout(timer);
  }, [justChanged]);

  const startEdit = () => {
    setDraft(String(value));
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const next = Number(draft);
    if (Number.isFinite(next) && next !== value) {
      onCommit(next);
      setJustChanged(true);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type='text'
        className='pricing-modal__input'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === KEY.ENTER) {
            e.currentTarget.blur();
          } else if (e.key === KEY.ESC) {
            cancelledRef.current = true;
            e.currentTarget.blur();
          }
        }}
      />
    );
  }

  return (
    <button
      type='button'
      className='pricing-modal__editable'
      onClick={startEdit}
    >
      {display}
      {justChanged && (
        <Icon
          icon='confirm'
          hasIconClass={false}
          className='pricing-modal__check'
        />
      )}
    </button>
  );
};

export default EditableCell;
